/* ============================================================
   TEAK TALE THEME by Impulsebyte (impulsebyte.com) — collection.js
   Handles: filter drawer, sort, view toggle, load more
   ============================================================ */
(function () {
'use strict';

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

/* ─── Filter Drawer (all breakpoints) ─── */
class FilterDrawer {
  constructor() {
    this.drawer = $('#FilterDrawer');
    this.form = $('#FilterForm');
    this.openBtns = $$('[data-open-filters]');
    this.closeBtns = $$('[data-close-filters]');
    if (!this.drawer) return;
    this._init();
  }
  _init() {
    this.openBtns.forEach(b => on(b, 'click', () => this.open()));
    this.closeBtns.forEach(b => on(b, 'click', () => this.close()));
    on(document, 'tt:backdrop:click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });

    // Submit builds a clean query string from the checked inputs, so several
    // filters can be chosen before a single page load (instead of reloading on
    // every tick, which lost the shopper's place in the list).
    on(this.form, 'submit', e => {
      e.preventDefault();
      const params = new URLSearchParams();
      new FormData(this.form).forEach((val, key) => {
        if (String(val).trim() !== '') params.append(key, val);
      });
      const base = window.location.pathname;
      window.location.href = params.toString() ? `${base}?${params}` : base;
    });

    // Live count of selected filters on the button
    on(this.form, 'change', () => this._syncCount());
    this._syncCount();
  }
  _syncCount() {
    if (!this.form) return;
    const n = $$('.filter-check:checked', this.form).length;
    const applyBtn = $('[data-apply-filters]');
    if (applyBtn) applyBtn.textContent = n > 0 ? `Show results (${n})` : 'Show results';
  }
  open() {
    if (!this.drawer || this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false'); this.drawer.inert = false;
    window.backdrop?.show();
    this.openBtns.forEach(b => b.setAttribute('aria-expanded', 'true'));
  }
  close() {
    if (!this.drawer?.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true'); this.drawer.inert = true;
    window.backdrop?.hide();
    this.openBtns.forEach(b => b.setAttribute('aria-expanded', 'false'));
  }
}

/* ─── Sort Dropdown ─── */
class SortDropdown {
  constructor() {
    this.btn = $('#SortBtn');
    this.menu = $('#SortMenu');
    this._init();
  }
  _init() {
    on(this.btn, 'click', () => { const o = this.menu?.classList.toggle('is-open'); this.btn.setAttribute('aria-expanded', o ? 'true' : 'false'); });
    on(document, 'click', e => { if (!e.target.closest('#SortBtn') && !e.target.closest('#SortMenu')) this.menu?.classList.remove('is-open'); });
    $$('.sort-menu__item', this.menu).forEach(item => {
      on(item, 'click', () => {
        const val = item.dataset.sort;
        const label = item.textContent.trim();
        if (this.btn) { const span = this.btn.querySelector('span'); if (span) span.textContent = 'Sort: ' + label; }
        $$('.sort-menu__item').forEach(i => i.classList.remove('is-active'));
        item.classList.add('is-active');
        this.menu?.classList.remove('is-open');
        // Update URL and reload
        const url = new URL(window.location);
        url.searchParams.set('sort_by', val);
        window.location.href = url.toString();
      });
    });
  }
}

/* ─── View Toggle (grid / list) ─── */
class ViewToggle {
  constructor() {
    this.grid = $('#ProductGrid');
    this.btns = $$('[data-view]');
    const saved = localStorage.getItem('tt_grid_view') || 'grid';
    this._set(saved);
    this.btns.forEach(b => on(b, 'click', () => this._set(b.dataset.view)));
  }
  _set(v) {
    localStorage.setItem('tt_grid_view', v);
    this.btns.forEach(b => b.classList.toggle('is-active', b.dataset.view === v));
    if (this.grid) { this.grid.dataset.viewMode = v; }
  }
}

/* ─── Filter Panel Accordions ─── */
$$('.filter-panel__header').forEach(header => {
  on(header, 'click', () => {
    const panel = header.closest('.filter-panel');
    const collapsed = panel?.classList.toggle('is-collapsed');
    header.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  });
});

/* ─── Subcategory Pills ─── */
$$('.cat-pill').forEach(pill => {
  on(pill, 'click', () => {
    $$('.cat-pill').forEach(p => p.classList.remove('is-active'));
    pill.classList.add('is-active');
    const tag = pill.dataset.tag;
    // If it's a URL navigation, follow it
    if (pill.dataset.href) { window.location.href = pill.dataset.href; }
  });
});

/* ─── Load More / Infinite Scroll ─── */
const loadMoreBtn = $('#LoadMoreBtn');
if (loadMoreBtn) {
  let loading = false;
  const loadNext = async () => {
    const nextUrl = loadMoreBtn.dataset.nextUrl;
    if (!nextUrl || loading) return;
    loading = true;
    loadMoreBtn.textContent = 'Loading…';
    try {
      const res = await fetch(nextUrl);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCards = $$('.prod-card', doc);
      const grid = $('#ProductGrid');
      newCards.forEach(card => grid?.appendChild(card));
      // Update load more button
      const newNext = doc.querySelector('[data-next-url]')?.dataset.nextUrl;
      if (newNext) loadMoreBtn.dataset.nextUrl = newNext;
      else { loadMoreBtn.style.display = 'none'; loadMoreBtn.dataset.nextUrl = ''; }
      loadMoreBtn.textContent = 'Load More';
    } catch(e) { loadMoreBtn.textContent = 'Load More'; }
    loading = false;
  };
  on(loadMoreBtn, 'click', loadNext);
  // Infinite scroll mode: auto-load when the button nears the viewport (IntersectionObserver)
  if (loadMoreBtn.dataset.infinite === 'true' && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) loadNext();
    }, { rootMargin: '400px 0px' });
    io.observe(loadMoreBtn);
  }
}

/* ─── Collection header: true lowest price for collections with more than 50 products ─── */
const floorEl = document.querySelector('[data-floor-url]');
if (floorEl) {
  const run = () => fetch(floorEl.dataset.floorUrl).then(r => r.text()).then(html => {
    const v = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-price-floor-value]')?.textContent.trim();
    if (v) floorEl.textContent = v;
  }).catch(() => {});
  ('requestIdleCallback' in window) ? requestIdleCallback(run, { timeout: 2500 }) : setTimeout(run, 800);
}

document.addEventListener('DOMContentLoaded', () => {
  new FilterDrawer();
  new SortDropdown();
  new ViewToggle();
});

})(); // end IIFE
